import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './Sidebar.css';
import productApi from "../../services/productApi";

import { PRODUCT_IMAGES } from '../scenario-planner/constants';

const FallbackIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="gsp-product-card-fallback-icon">
        <path d="M6 3h12l1 6H5l1-6z" />
        <path d="M5 9h14v10a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V9z" />
    </svg>
);

// The Sidebar component now accepts a new prop for handling product selection
const Sidebar = ({ isOpen, onClose, onProductSelectionChange }) => {
    const { userType } = useAuth();
    const location = useLocation();

    // State to hold the list of products fetched from the API
    const [products, setProducts] = useState([]);

    // State to hold the IDs of the currently selected products
    const [selectedProductIds, setSelectedProductIds] = useState([]);
    const [filters] = useState({
        skip: 0,
        limit: 50,
        active_only: false,
        flavor: '',
        recipe: true,
        product_ids:[],
        sort: 'flavor:asc'
    });
    // 1. Fetch products from the API endpoint
    useEffect(() => {
        const fetchProducts = async () => {
            try {
                // NOTE: In a real application, you'd use a more robust fetch wrapper (e.g., Axios)
                // and handle authentication headers. We use a simple fetch here.
                const response = await productApi.list({filters})
                if (response) {
                    setProducts(response.items || []);
                }
            } catch (error) {
                console.error("Failed to fetch products:", error);
                // Set an empty array on error to prevent crashing
                setProducts([]);
            }
        };

        fetchProducts();
    }, [filters]); // Empty dependency array means this runs only once on mount

    // 2. Close sidebar on route change (mobile)
    useEffect(() => {
        if (window.innerWidth < 768) {
            onClose();
        }
    }, [location, onClose]);

    // 3. Handle product selection (multi-select/toggle)
    const handleProductClick = (productId) => {
        let newSelectedIds;

        if (selectedProductIds.includes(productId)) {
            // Deselect: remove the ID
            newSelectedIds = selectedProductIds.filter(id => id !== productId);
        } else {
            // Select: add the ID
            newSelectedIds = [...selectedProductIds, productId];
        }

        setSelectedProductIds(newSelectedIds);

        // Call the provided callback function to pass the selection back to the parent component
        if (onProductSelectionChange) {
            onProductSelectionChange(newSelectedIds);
        }
    };

    const handleClearAll = () => {
        setSelectedProductIds([]);
        if (onProductSelectionChange) {
            onProductSelectionChange([]);
        }
    };

    return (
        <>
            {/* Overlay for mobile */}
            {isOpen && (
                <div
                    className="sidebar-overlay"
                    onClick={onClose}
                    aria-hidden="true"
                />
            )}

            <aside className={`app-sidebar ${isOpen ? 'open' : ''} gsp-app-sidebar`} role="navigation" aria-label="Product selector">
                <div className="sidebar-content gsp-sidebar-content">

                    <div className="gsp-sidebar-header">
                        <h3 className="gsp-sidebar-title">Product Workspace</h3>
                        <span className="gsp-sidebar-subtitle">Select flavors to plan</span>
                    </div>

                    <nav className="gsp-sidebar-nav">
                        <ul className="gsp-product-list">
                            {products.length > 0 ? (
                                products.map((product) => {
                                    const isSelected = selectedProductIds.includes(product.id);
                                    const normName = product.flavor?.toLowerCase().trim();
                                    const productImg = PRODUCT_IMAGES[normName];
                                    return (
                                        <li key={product.id} className="gsp-product-item">
                                            <div
                                                onClick={() => handleProductClick(product.id)}
                                                className={`gsp-product-card ${isSelected ? 'selected' : ''}`}
                                                role="button"
                                                tabIndex="0"
                                                aria-pressed={isSelected}
                                            >
                                                <div className="gsp-product-card-thumbnail-wrapper">
                                                    {productImg ? (
                                                        <img src={productImg} alt="" className="gsp-product-card-thumbnail" />
                                                    ) : (
                                                        <FallbackIcon />
                                                    )}
                                                </div>
                                                <span className="gsp-product-card-label">{product.flavor}</span>
                                                <div className="gsp-product-card-indicator">
                                                    {isSelected && (
                                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                                            <polyline points="20 6 9 17 4 12" />
                                                        </svg>
                                                    )}
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })
                            ) : (
                                <li className="gsp-product-item-loading">Loading products...</li>
                            )}
                        </ul>
                    </nav>

                    <div className="gsp-sidebar-footer">
                        {selectedProductIds.length > 0 && (
                            <button className="gsp-sidebar-clear-btn" onClick={handleClearAll}>
                                Clear Selection ({selectedProductIds.length})
                            </button>
                        )}
                        <div className="gsp-role-indicator">
                            <span className="gsp-role-text">
                                {userType ? `Workspace: ${userType}` : 'No workspace assigned'}
                            </span>
                        </div>
                    </div>
                </div>
            </aside>
        </>
    );
};

export default Sidebar;